import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, query, where } from 'firebase/firestore';
import { db } from './firebase';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import './HOTshirtRecon.css';

function HOTshirtRecon({ onClose }) {
  const [loading, setLoading]           = useState(true);
  const [franchisees, setFranchisees]   = useState([]);   // all Franchisees docs
  const [orders, setOrders]             = useState([]);    // all tshirtOrders
  const [stockItems, setStockItems]     = useState([]);    // all tshirtStock

  // Filters
  const [filterFranchisee, setFilterFranchisee] = useState('all');
  const [filterArea, setFilterArea]             = useState('all');

  // Expand/collapse per franchisee
  const [expandedFranchisee, setExpandedFranchisee] = useState(null);

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    try {
      // 1. All franchisees
      const frSnap = await getDocs(collection(db, 'Franchisees'));
      const frList = frSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      frList.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setFranchisees(frList);

      // 2. All T-shirt orders
      const ordSnap = await getDocs(collection(db, 'tshirtOrders'));
      const ordList = ordSnap.docs.map(d => {
        const data = d.data();
        // Resolve franchiseeRef → franchiseeId string
        let franchiseeId = null;
        if (data.franchiseeRef) {
          franchiseeId = data.franchiseeRef.id
            || data.franchiseeRef?.path?.split('/').pop()
            || null;
        }
        return {
          id: d.id,
          ...data,
          franchiseeId,
          orderDateParsed: data.orderDate?.toDate
            ? data.orderDate.toDate()
            : new Date(data.orderDate || Date.now())
        };
      });
      ordList.sort((a, b) => a.orderDateParsed - b.orderDateParsed);
      setOrders(ordList);

      // 3. All T-shirt stock
      const stSnap = await getDocs(collection(db, 'tshirtStock'));
      const stList = stSnap.docs.map(d => {
        const data = d.data();
        let franchiseeId = null;
        if (data.franchiseeRef) {
          franchiseeId = data.franchiseeRef.id
            || data.franchiseeRef?.path?.split('/').pop()
            || null;
        }
        return { id: d.id, ...data, franchiseeId };
      });
      setStockItems(stList);

    } catch (err) {
      console.error('Error loading data:', err);
      alert('Error loading data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ── Helpers ──
  const fmt = (n) => `R${(n || 0).toFixed(2)}`;
  const fmtDate = (d) => {
    if (!d) return '—';
    const dt = d instanceof Date ? d : new Date(d);
    return isNaN(dt) ? '—' : dt.toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  // Unique territories from franchisees
  const allAreas = [...new Set(
    franchisees.map(f => (f.territory || '').trim()).filter(Boolean)
  )].sort();

  // Filter logic
  const visibleFranchisees = franchisees.filter(f => {
    if (filterArea !== 'all' && (f.territory || '').trim() !== filterArea) return false;
    if (filterFranchisee !== 'all' && f.id !== filterFranchisee) return false;
    return true;
  });

  // Orders and stock grouped by franchiseeId
  const ordersByFr  = {};
  orders.forEach(o => {
    if (!ordersByFr[o.franchiseeId]) ordersByFr[o.franchiseeId] = [];
    ordersByFr[o.franchiseeId].push(o);
  });

  const stockByFr = {};
  stockItems.forEach(s => {
    if (!stockByFr[s.franchiseeId]) stockByFr[s.franchiseeId] = [];
    stockByFr[s.franchiseeId].push(s);
  });

  // Stock grouped by order inside each franchisee
  const stockByOrder = {};
  stockItems.forEach(s => {
    if (!stockByOrder[s.sourceOrderId]) stockByOrder[s.sourceOrderId] = [];
    stockByOrder[s.sourceOrderId].push(s);
  });

  // Per-franchisee totals
  const frTotals = (frId) => {
    const frOrders = ordersByFr[frId] || [];
    const frStock  = stockByFr[frId]  || [];
    return {
      totalPaid:     frOrders.reduce((s, o) => s + (o.totalCost || 0), 0),
      newShirts:     frOrders.reduce((s, o) => s + (o.studentTshirtCount || 0) + (o.extraStockCount || 0), 0),
      stockAllocated:frStock.reduce((s, x) => s + (x.allocated || 0), 0),
      stockRemaining:frStock.reduce((s, x) => s + (x.remaining || 0), 0),
      orderCount:    frOrders.length
    };
  };

  // Grand totals across visible franchisees
  const grandTotals = visibleFranchisees.reduce((acc, f) => {
    const t = frTotals(f.id);
    acc.totalPaid      += t.totalPaid;
    acc.newShirts      += t.newShirts;
    acc.stockAllocated += t.stockAllocated;
    acc.stockRemaining += t.stockRemaining;
    acc.orderCount     += t.orderCount;
    return acc;
  }, { totalPaid: 0, newShirts: 0, stockAllocated: 0, stockRemaining: 0, orderCount: 0 });


  // ── Excel Export — 3 tabs, fully styled ──
  const exportToExcel = async () => {
    const wb = new ExcelJS.Workbook();
    wb.creator = 'BABS Head Office';
    wb.created = new Date();

    const C = {
      darkPurple:'1A1A6E', midPurple:'4C2E8B', lightPurple:'EDE9FE',
      blue:'29ABE2',       lightBlue:'E0F2FE',
      green:'39B54A',      lightGreen:'DCFCE7',
      orange:'F7941D',     lightOrange:'FFF7ED',
      red:'ED1C24',        lightRed:'FEE2E2',
      yellow:'FFF200',     white:'FFFFFF',
      greyHeader:'F3F4F6', darkText:'1F2937', dimText:'6B7280',
    };

    const ORDER_THEMES = [
      { hdr: C.midPurple, light: C.lightPurple, size: '7C3AED' },
      { hdr: C.blue,      light: C.lightBlue,   size: '0284C7' },
      { hdr: C.green,     light: C.lightGreen,  size: '16A34A' },
      { hdr: 'B45309',    light: 'FEF3C7',      size: 'D97706' },
      { hdr: 'BE185D',    light: 'FCE7F3',      size: 'DB2777' },
    ];

    const cx = (ws, row, col, value, opts = {}) => {
      const c = ws.getCell(row, col);
      c.value = value;
      if (opts.bg) c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF' + opts.bg } };
      const fontBase = { size: opts.size || 10 };
      if (opts.color) fontBase.color = { argb: 'FF' + opts.color };
      if (opts.bold)  fontBase.bold  = true;
      if (opts.italic) fontBase.italic = true;
      c.font = fontBase;
      c.alignment = { horizontal: opts.align || 'left', vertical: 'middle', wrapText: true };
      if (opts.border) {
        const s = { style: opts.border === 'hair' ? 'hair' : 'thin' };
        c.border = { top: s, bottom: s, left: s, right: s };
      }
      if (opts.numFmt) c.numFmt = opts.numFmt;
      return c;
    };

    const mg = (ws, r1, c1, r2, c2) => ws.mergeCells(r1, c1, r2, c2);

    // ════════════════════════════
    // TAB 1 — ORDER SUMMARY
    // ════════════════════════════
    const ws1 = wb.addWorksheet('Order Summary');
    ws1.views = [{ showGridLines: false }];
    ws1.columns = [{width:2},{width:28},{width:20},{width:18},{width:22},{width:22},{width:22},{width:18},{width:2}];

    let r = 1;

    ws1.getRow(r).height = 36;
    mg(ws1,r,2,r,8);
    cx(ws1,r,2,'👕  T-Shirt Order Summary — Per Franchisee',{bg:C.darkPurple,bold:true,color:C.white,size:15});
    r++;
    ws1.getRow(r).height = 20;
    mg(ws1,r,2,r,8);
    cx(ws1,r,2,'This sheet shows exactly what was ordered, what was distributed, and what is still sitting in stock for each order.',{bg:C.lightPurple,italic:true,color:C.darkText});
    r += 2;

    ws1.getRow(r).height = 30;
    ['','Order','Order Date','Student Shirts\n(billed)','Extra Stock\nOrdered (billed)','Extra Stock\nAllocated','Still On Hand\n(in stock)','Amount\nCharged','']
      .forEach((h,i)=>{ if(h) cx(ws1,r,i+1,h,{bg:C.midPurple,bold:true,color:C.white,size:10,align:'center',border:'thin'}); });
    r++;

    visibleFranchisees.forEach(f => {
      const frOrders = ordersByFr[f.id] || [];
      if (frOrders.length === 0) return;
      r++;
      ws1.getRow(r).height = 24;
      mg(ws1,r,2,r,8);
      cx(ws1,r,2,`  ${f.name}  ·  ${f.territory||'No territory'}`,{bg:C.darkPurple,bold:true,color:C.white,size:12});
      r++;

      frOrders.forEach((order,idx) => {
        const theme         = ORDER_THEMES[idx % ORDER_THEMES.length];
        const sfO           = stockByOrder[order.id] || [];
        const extraEntries  = order.extraStock ? Object.entries(order.extraStock).filter(([,q])=>q>0) : [];
        const extraOrd      = extraEntries.reduce((s,[,q])=>s+q, 0);
        const extraAlloc    = sfO.reduce((s,x)=>s+(x.allocated||0), 0);
        const onHand        = sfO.reduce((s,x)=>s+(x.remaining||0), 0);
        const students      = order.studentTshirtCount || (order.studentTshirts||[]).length;
        const pct           = extraOrd>0 ? Math.round((extraAlloc/extraOrd)*100) : 0;

        // Order header bar
        ws1.getRow(r).height = 26;
        mg(ws1,r,2,r,8);
        cx(ws1,r,2,`  Order ${idx+1}  ·  ${order.orderNumber||''}  ·  ${fmtDate(order.orderDateParsed)}`,{bg:theme.hdr,bold:true,color:C.white,size:12});
        r++;

        // Data row
        ws1.getRow(r).height = 30;
        [
          ['',null],
          [`Order ${idx+1}`,{bg:theme.light,bold:true,color:C.darkText,border:'thin',size:11}],
          [fmtDate(order.orderDateParsed),{bg:theme.light,color:C.dimText,border:'thin',align:'center'}],
          [students,{bg:theme.light,bold:true,color:C.midPurple,border:'thin',align:'center',size:13}],
          [extraOrd,{bg:theme.light,bold:true,color:C.darkText,border:'thin',align:'center',size:13}],
          [extraAlloc,{bg:theme.light,bold:true,color:pct<100&&extraOrd>0?C.orange:C.green,border:'thin',align:'center',size:13}],
          [onHand,{bg:theme.light,bold:true,color:onHand>0?C.red:C.green,border:'thin',align:'center',size:13}],
          [order.totalCost||0,{bg:theme.light,bold:true,color:C.red,border:'thin',align:'center',numFmt:'R#,##0.00',size:13}],
          ['',null],
        ].forEach(([v,opts],i)=>{ if(opts) cx(ws1,r,i+1,v,opts); });
        r++;

        // Sentence row
        ws1.getRow(r).height = 18;
        mg(ws1,r,2,r,8);
        let sent = `${students} student shirts ordered & billed  ·  ${extraOrd} extra buffer shirts ordered & billed`;
        if(extraOrd>0){
          if(onHand===0)           sent += `  →  All ${extraOrd} extra shirts fully allocated. Nothing in stock.`;
          else if(extraAlloc===0)  sent += `  →  ⚠️ None of the ${extraOrd} extra shirts allocated yet — all ${onHand} sitting in stock.`;
          else                     sent += `  →  ${extraAlloc} allocated. ${onHand} shirts still sitting in stock.`;
        }
        cx(ws1,r,2,sent,{bg:C.greyHeader,italic:true,color:C.darkText,size:9});
        r++;

        // Size breakdown
        if(extraEntries.length > 0){
          ws1.getRow(r).height = 18;
          ['','Size','','Ordered','Allocated','Remaining','','','Status'].forEach((h,i)=>{
            if(h) cx(ws1,r,i+1,h,{bg:theme.size,bold:true,color:C.white,size:9,align:'center',border:'thin'});
            else  cx(ws1,r,i+1,'',{bg:theme.light});
          });
          r++;

          extraEntries.forEach(([size,ordered])=>{
            const sd        = sfO.find(s=>s.size===size);
            const allocated = sd?(sd.allocated||0):0;
            const remaining = sd?(sd.remaining||0):ordered;
            const statusTxt = remaining===0?'✅ Fully used':allocated===0?'⚠️ None allocated yet':`🔶 ${remaining} remaining`;
            ws1.getRow(r).height = 18;
            [
              ['',null],
              [size,{bg:theme.light,bold:true,color:theme.size,border:'hair'}],
              ['',{bg:theme.light}],
              [ordered,  {bg:theme.light,bold:true,color:C.darkText,align:'center',border:'hair'}],
              [allocated,{bg:theme.light,bold:true,color:C.blue,align:'center',border:'hair'}],
              [remaining,{bg:theme.light,bold:true,color:remaining>0?C.red:C.green,align:'center',border:'hair'}],
              ['',{bg:theme.light}],
              ['',{bg:theme.light}],
              [statusTxt,{bg:theme.light,color:C.darkText,size:9,border:'hair'}],
            ].forEach(([v,opts],i)=>{ if(opts) cx(ws1,r,i+1,v,opts); });
            r++;
          });

          // Totals row
          ws1.getRow(r).height = 18;
          const totOrd = extraEntries.reduce((s,[,q])=>s+q,0);
          [
            ['',null],
            ['TOTAL',{bg:theme.light,bold:true,color:theme.size,border:'thin'}],
            ['',{bg:theme.light}],
            [totOrd,   {bg:theme.light,bold:true,color:C.darkText,align:'center',border:'thin'}],
            [extraAlloc,{bg:theme.light,bold:true,color:C.blue,align:'center',border:'thin'}],
            [onHand,   {bg:theme.light,bold:true,color:onHand>0?C.red:C.green,align:'center',border:'thin'}],
            ['',{bg:theme.light}],['',{bg:theme.light}],['',{bg:theme.light}],
          ].forEach(([v,opts],i)=>{ if(opts) cx(ws1,r,i+1,v,opts); });
          r++;
        }

        r++; // spacer
      });

      // Franchisee total
      ws1.getRow(r).height = 28;
      const ft = frTotals(f.id);
      mg(ws1,r,2,r,7);
      cx(ws1,r,2,`  💳  Total paid by ${f.name} across all ${ft.orderCount} order${ft.orderCount!==1?'s':''}`,{bg:C.darkPurple,bold:true,color:C.white,size:12});
      cx(ws1,r,8,ft.totalPaid,{bg:C.darkPurple,bold:true,color:C.yellow,size:14,align:'center',numFmt:'R#,##0.00'});
      r += 2;
    });

    // Stock alert
    const frWithStock = visibleFranchisees.filter(f=>(stockByFr[f.id]||[]).some(x=>x.remaining>0));
    if(frWithStock.length > 0){
      ws1.getRow(r).height = 24;
      mg(ws1,r,2,r,8);
      cx(ws1,r,2,'⚠️  STOCK ON HAND ALERT',{bg:C.orange,bold:true,color:C.white,size:12,align:'center'});
      r++;
      frWithStock.forEach(f=>{
        const onHand = (stockByFr[f.id]||[]).reduce((s,x)=>s+(x.remaining||0),0);
        ws1.getRow(r).height = 18;
        mg(ws1,r,2,r,8);
        cx(ws1,r,2,`  ${f.name}: ${onHand} shirts paid for but not yet allocated to a student`,{bg:C.lightOrange,color:'7C2D12',size:10});
        r++;
      });
      const totalUnalloc = frWithStock.reduce((s,f)=>(stockByFr[f.id]||[]).reduce((a,x)=>a+(x.remaining||0),0)+s,0);
      ws1.getRow(r).height = 22;
      mg(ws1,r,2,r,8);
      cx(ws1,r,2,`  ❓ Were the extra shirts in the latest order necessary when there were already ${totalUnalloc} unallocated shirts in stock?`,{bg:C.lightRed,bold:true,color:C.red,size:10});
    }

    // ════════════════════════════
    // TAB 2 — SUMMARY
    // ════════════════════════════
    const ws2 = wb.addWorksheet('Summary');
    ws2.views = [{ showGridLines: false }];
    ws2.columns = [{width:2},{width:28},{width:16},{width:10},{width:20},{width:22},{width:22},{width:18},{width:2}];
    let r2=1;

    ws2.getRow(r2).height = 32;
    mg(ws2,r2,2,r2,8);
    cx(ws2,r2,2,'👕  T-Shirt Reconciliation — Summary',{bg:C.darkPurple,bold:true,color:C.white,size:15});
    r2++;
    ws2.getRow(r2).height = 18;
    mg(ws2,r2,2,r2,8);
    cx(ws2,r2,2,`Generated: ${fmtDate(new Date())}`,{bg:C.lightPurple,italic:true,color:C.dimText});
    r2+=2;

    ws2.getRow(r2).height = 28;
    ['','Franchisee','Territory','Orders','Student Shirts','Extra Ordered','Extra Allocated','Still On Hand','Total Paid']
      .forEach((h,i)=>{ if(h) cx(ws2,r2,i+1,h,{bg:C.midPurple,bold:true,color:C.white,size:10,align:'center',border:'thin'}); });
    r2++;

    visibleFranchisees.forEach((f,fi)=>{
      const frOrders   = ordersByFr[f.id]||[];
      const frStock    = stockByFr[f.id]||[];
      const students   = frOrders.reduce((s,o)=>s+(o.studentTshirtCount||(o.studentTshirts||[]).length),0);
      const extraOrd   = frOrders.reduce((s,o)=>s+(o.extraStock?Object.values(o.extraStock).reduce((a,v)=>a+v,0):0),0);
      const extraAlloc = frStock.reduce((s,x)=>s+(x.allocated||0),0);
      const onHand     = frStock.reduce((s,x)=>s+(x.remaining||0),0);
      const paid       = frOrders.reduce((s,o)=>s+(o.totalCost||0),0);
      const bg         = fi%2===0?C.white:C.greyHeader;
      ws2.getRow(r2).height = 22;
      [
        ['',null],
        [f.name||'—',{bg,bold:true,color:C.darkText,border:'thin'}],
        [f.territory||'—',{bg,color:C.dimText,border:'thin',align:'center'}],
        [frOrders.length,{bg,color:C.darkText,border:'thin',align:'center'}],
        [students,{bg,bold:true,color:C.midPurple,border:'thin',align:'center',size:12}],
        [extraOrd,{bg,bold:true,color:C.darkText,border:'thin',align:'center',size:12}],
        [extraAlloc,{bg,bold:true,color:extraAlloc===extraOrd&&extraOrd>0?C.green:C.orange,border:'thin',align:'center',size:12}],
        [onHand,{bg,bold:true,color:onHand>0?C.red:C.green,border:'thin',align:'center',size:12}],
        [paid,{bg,bold:true,color:C.red,border:'thin',align:'center',numFmt:'R#,##0.00'}],
      ].forEach(([v,opts],i)=>{ if(opts) cx(ws2,r2,i+1,v,opts); });
      r2++;
    });

    ws2.getRow(r2).height = 28;
    const gStudents = visibleFranchisees.reduce((s,f)=>(ordersByFr[f.id]||[]).reduce((a,o)=>a+(o.studentTshirtCount||(o.studentTshirts||[]).length),0)+s,0);
    const gExtra    = visibleFranchisees.reduce((s,f)=>(ordersByFr[f.id]||[]).reduce((a,o)=>a+(o.extraStock?Object.values(o.extraStock).reduce((x,v)=>x+v,0):0),0)+s,0);
    [
      ['',null],
      ['TOTALS',{bg:C.darkPurple,bold:true,color:C.white,border:'thin',size:12}],
      ['',{bg:C.darkPurple,border:'thin'}],
      [grandTotals.orderCount,{bg:C.darkPurple,bold:true,color:C.white,border:'thin',align:'center'}],
      [gStudents,{bg:C.darkPurple,bold:true,color:C.yellow,border:'thin',align:'center',size:13}],
      [gExtra,{bg:C.darkPurple,bold:true,color:C.yellow,border:'thin',align:'center',size:13}],
      [grandTotals.stockAllocated,{bg:C.darkPurple,bold:true,color:C.yellow,border:'thin',align:'center',size:13}],
      [grandTotals.stockRemaining,{bg:C.darkPurple,bold:true,color:grandTotals.stockRemaining>0?'FCA5A5':C.yellow,border:'thin',align:'center',size:13}],
      [grandTotals.totalPaid,{bg:C.darkPurple,bold:true,color:C.yellow,border:'thin',align:'center',numFmt:'R#,##0.00',size:13}],
    ].forEach(([v,opts],i)=>{ if(opts) cx(ws2,r2,i+1,v,opts); });

    // ════════════════════════════
    // TAB 3 — FULL DETAIL
    // ════════════════════════════
    const ws3 = wb.addWorksheet('Full Detail');
    ws3.views = [{ showGridLines: false }];
    ws3.columns = [{width:2},{width:22},{width:14},{width:20},{width:14},{width:28},{width:26},{width:14},{width:38},{width:14},{width:18}];
    let r3=1;

    ws3.getRow(r3).height = 32;
    mg(ws3,r3,2,r3,11);
    cx(ws3,r3,2,'👕  T-Shirt Reconciliation — Full Detail',{bg:C.darkPurple,bold:true,color:C.white,size:15});
    r3+=2;

    ws3.getRow(r3).height = 26;
    ['','Franchisee','Territory','Order #','Order Date','Student Name','School','Size','Source','Date','Total Charged']
      .forEach((h,i)=>{ if(h) cx(ws3,r3,i+1,h,{bg:C.midPurple,bold:true,color:C.white,size:10,align:'center',border:'thin'}); });
    r3++;

    visibleFranchisees.forEach((f,fi)=>{
      (ordersByFr[f.id]||[]).forEach((order,idx)=>{
        const sfO   = stockByOrder[order.id]||[];
        const theme = ORDER_THEMES[idx%ORDER_THEMES.length];
        const bg    = fi%2===0?C.white:C.greyHeader;

        (order.studentTshirts||[]).forEach(st=>{
          ws3.getRow(r3).height = 18;
          ['',f.name||'—',f.territory||'—',order.orderNumber||`Order ${idx+1}`,fmtDate(order.orderDateParsed),
           st.studentName,st.schoolName||'—',st.size,'New Order (billed)',fmtDate(order.orderDateParsed),order.totalCost||0]
          .forEach((v,i)=>cx(ws3,r3,i+1,v,{bg,color:i===10?C.red:C.darkText,border:'hair',numFmt:i===10?'R#,##0.00':undefined}));
          r3++;
        });

        if(order.extraStock){
          Object.entries(order.extraStock).filter(([,q])=>q>0).forEach(([size,qty])=>{
            const sd=sfO.find(s=>s.size===size);
            const allocated=sd?(sd.allocated||0):0;
            const remaining=sd?(sd.remaining||0):qty;
            ws3.getRow(r3).height = 18;
            ['',f.name||'—',f.territory||'—',order.orderNumber||`Order ${idx+1}`,fmtDate(order.orderDateParsed),
             `EXTRA STOCK — ${size} (${qty} ordered, ${allocated} allocated, ${remaining} remaining)`,
             '—',size,'Extra Stock (billed)',fmtDate(order.orderDateParsed),'']
            .forEach((v,i)=>cx(ws3,r3,i+1,v,{bg:theme.light,color:C.darkText,border:'hair'}));
            r3++;
          });
        }

        sfO.forEach(sd=>{
          (sd.allocations||[]).forEach(alloc=>{
            const allocDate=alloc.allocatedDate?.toDate?alloc.allocatedDate.toDate():alloc.allocatedDate?new Date(alloc.allocatedDate):null;
            ws3.getRow(r3).height = 18;
            ['',f.name||'—',f.territory||'—',order.orderNumber||`Order ${idx+1}`,fmtDate(order.orderDateParsed),
             alloc.studentName,'—',sd.size,'From Stock (no charge)',fmtDate(allocDate),0]
            .forEach((v,i)=>cx(ws3,r3,i+1,v,{bg:C.lightBlue,color:i===10?C.green:C.darkText,border:'hair'}));
            r3++;
          });
        });
      });
    });

    // Save
    const territory  = filterArea !== 'all' ? `_${filterArea}` : '';
    const frName     = filterFranchisee !== 'all'
      ? `_${(franchisees.find(f=>f.id===filterFranchisee)?.name||'').replace(/\s+/g,'_')}` : '';
    const filename = `TShirt_Recon${territory}${frName}_${new Date().toISOString().split('T')[0]}.xlsx`;
    const buffer = await wb.xlsx.writeBuffer();
    saveAs(new Blob([buffer],{type:'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'}), filename);
  };


  if (loading) {
    return (
      <div className="horecon-page">
        <div className="horecon-loading">
          <div className="horecon-spinner" />
          <p>Loading all franchisee data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="horecon-page">

      {/* ── HEADER ── */}
      <div className="horecon-header">
        <div>
          <h1>👕 T-Shirt Reconciliation — All Franchisees</h1>
          <p>Head Office Overview &nbsp;·&nbsp; Generated: {fmtDate(new Date())}</p>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '10px' }}>
          <div className="horecon-header-total">
            <div className="hrt-label">Total Paid (Visible)</div>
            <div className="hrt-value">{fmt(grandTotals.totalPaid)}</div>
          </div>
          <button className="excel-btn" onClick={exportToExcel}>
            📥 Download Excel
          </button>
        </div>
      </div>

      {/* ── FILTERS ── */}
      <div className="horecon-filters">
        <div className="filter-group">
          <label>Filter by Territory</label>
          <select
            value={filterArea}
            onChange={e => { setFilterArea(e.target.value); setFilterFranchisee('all'); }}
          >
            <option value="all">All Areas</option>
            {allAreas.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
        </div>

        <div className="filter-group">
          <label>Filter by Franchisee</label>
          <select
            value={filterFranchisee}
            onChange={e => setFilterFranchisee(e.target.value)}
          >
            <option value="all">All Franchisees</option>
            {(filterArea === 'all' ? franchisees : franchisees.filter(f => (f.territory || '').trim() === filterArea))
              .map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
          </select>
        </div>

        <div className="filter-results-badge">
          Showing <strong>{visibleFranchisees.length}</strong> franchisee{visibleFranchisees.length !== 1 ? 's' : ''}
        </div>
      </div>

      {/* ── SUMMARY CARDS ── */}
      <div className="horecon-summary-cards">
        <div className="hsc blue">
          <div className="hsc-icon">📋</div>
          <div className="hsc-label">Total Orders</div>
          <div className="hsc-value">{grandTotals.orderCount}</div>
        </div>
        <div className="hsc green">
          <div className="hsc-icon">🆕</div>
          <div className="hsc-label">New Shirts Billed</div>
          <div className="hsc-value">{grandTotals.newShirts}</div>
        </div>
        <div className="hsc purple">
          <div className="hsc-icon">📦</div>
          <div className="hsc-label">Stock Allocated (no charge)</div>
          <div className="hsc-value">{grandTotals.stockAllocated}</div>
        </div>
        <div className="hsc teal">
          <div className="hsc-icon">🏪</div>
          <div className="hsc-label">Still In Stock</div>
          <div className="hsc-value">{grandTotals.stockRemaining}</div>
        </div>
        <div className="hsc red">
          <div className="hsc-icon">💰</div>
          <div className="hsc-label">Total Revenue</div>
          <div className="hsc-value">{fmt(grandTotals.totalPaid)}</div>
        </div>
      </div>

      {/* ── QUICK OVERVIEW TABLE (all visible franchisees) ── */}
      <div className="horecon-section">
        <h2 className="horecon-section-title">📊 Quick Overview — All Franchisees</h2>
        <div className="table-scroll">
          <table className="hrt">
            <thead>
              <tr>
                <th>Franchisee</th>
                <th>Territory</th>
                <th>Orders</th>
                <th>New Shirts Billed</th>
                <th>Stock Allocated</th>
                <th>Stock Remaining</th>
                <th>Total Paid</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {visibleFranchisees.map(f => {
                const t = frTotals(f.id);
                const isExpanded = expandedFranchisee === f.id;
                const hasOrders = t.orderCount > 0;
                return (
                  <tr
                    key={f.id}
                    className={`fr-row ${hasOrders ? 'clickable' : ''} ${isExpanded ? 'expanded-row' : ''}`}
                    onClick={() => hasOrders && setExpandedFranchisee(isExpanded ? null : f.id)}
                  >
                    <td><strong>{f.name || '—'}</strong></td>
                    <td>{f.territory || <span className="dim">—</span>}</td>
                    <td>{t.orderCount > 0 ? t.orderCount : <span className="dim">0</span>}</td>
                    <td>
                      {t.newShirts > 0
                        ? <span className="badge-new">🆕 {t.newShirts}</span>
                        : <span className="dim">—</span>}
                    </td>
                    <td>
                      {t.stockAllocated > 0
                        ? <span className="badge-stock">📦 {t.stockAllocated}</span>
                        : <span className="dim">—</span>}
                    </td>
                    <td>
                      {t.stockRemaining > 0
                        ? <span className="badge-rem">{t.stockRemaining} in stock</span>
                        : <span className="dim">—</span>}
                    </td>
                    <td className="amt-cell">{t.totalPaid > 0 ? fmt(t.totalPaid) : <span className="dim">—</span>}</td>
                    <td>
                      {hasOrders && (
                        <button className="expand-btn">
                          {isExpanded ? '▲ Hide' : '▼ Detail'}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="grand-foot">
                <td colSpan="2"><strong>TOTALS ({visibleFranchisees.length} franchisees)</strong></td>
                <td><strong>{grandTotals.orderCount}</strong></td>
                <td><strong>{grandTotals.newShirts} shirts</strong></td>
                <td><strong>{grandTotals.stockAllocated} shirts</strong></td>
                <td><strong>{grandTotals.stockRemaining} shirts</strong></td>
                <td className="amt-cell grand-final"><strong>{fmt(grandTotals.totalPaid)}</strong></td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* ── EXPANDED DETAIL — per franchisee ── */}
      {expandedFranchisee && (() => {
        const fr       = franchisees.find(f => f.id === expandedFranchisee);
        const frOrders = ordersByFr[expandedFranchisee] || [];

        // Total unallocated stock across all orders
        const totalOnHand = (stockByFr[expandedFranchisee] || [])
          .reduce((s, x) => s + (x.remaining || 0), 0);

        // Per-order data for the summary cards
        const orderSummaries = frOrders.map((order, idx) => {
          const stockForOrder  = stockByOrder[order.id] || [];
          const extraEntries   = order.extraStock
            ? Object.entries(order.extraStock).filter(([, q]) => q > 0)
            : [];
          const extraOrdered   = extraEntries.reduce((s, [, q]) => s + q, 0);
          const extraAllocated = stockForOrder.reduce((s, x) => s + (x.allocated || 0), 0);
          const stillOnHand    = stockForOrder.reduce((s, x) => s + (x.remaining || 0), 0);
          const studentCount   = order.studentTshirtCount || (order.studentTshirts || []).length;

          // Size breakdown rows
          const sizeRows = extraEntries.map(([size, ordered]) => {
            const sd        = stockForOrder.find(s => s.size === size);
            const allocated = sd ? (sd.allocated || 0) : 0;
            const remaining = sd ? (sd.remaining || 0) : ordered;
            return { size, ordered, allocated, remaining };
          });

          return { order, idx, extraOrdered, extraAllocated, stillOnHand, studentCount, sizeRows };
        });

        return (
          <div className="horecon-section detail-section">
            <h2 className="horecon-section-title">
              📋 Order Summary — {fr?.name}
              {fr?.territory && <span className="area-tag">{fr.territory}</span>}
            </h2>

            {/* ── Stock alert if unallocated stock is building up ── */}
            {totalOnHand > 0 && (() => {
              const ordersWithStock = orderSummaries.filter(s => s.stillOnHand > 0);
              return (
                <div className="stock-alert-box">
                  <div className="sab-title">⚠️ Stock On Hand Alert</div>
                  <div className="sab-body">
                    <strong>{totalOnHand} shirts</strong> have been paid for but not yet allocated to a student:
                    <ul className="sab-list">
                      {ordersWithStock.map(s => (
                        <li key={s.order.id}>
                          Order {s.idx + 1} ({fmtDate(s.order.orderDateParsed)}):&nbsp;
                          <strong>{s.stillOnHand} shirts</strong> still in stock
                        </li>
                      ))}
                    </ul>
                    {ordersWithStock.length > 1 && (
                      <div className="sab-question">
                        ❓ Were the extra shirts in the latest order necessary, given there was already unallocated stock from previous orders?
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}

            {frOrders.length === 0 ? (
              <div className="horecon-empty">No orders for this franchisee.</div>
            ) : (
              orderSummaries.map(({ order, idx, extraOrdered, extraAllocated, stillOnHand, studentCount, sizeRows }) => {
                const pctAllocated = extraOrdered > 0
                  ? Math.round((extraAllocated / extraOrdered) * 100) : 0;

                return (
                  <div key={order.id} className="order-summary-card">

                    {/* ── Card header ── */}
                    <div className="osc-header">
                      <div className="osc-header-left">
                        <span className="osc-order-num">Order {idx + 1}</span>
                        <span className="osc-order-id">{order.orderNumber || ''}</span>
                      </div>
                      <div className="osc-header-right">
                        <span className="osc-date">{fmtDate(order.orderDateParsed)}</span>
                        <span className="osc-charged">{fmt(order.totalCost)}</span>
                      </div>
                    </div>

                    {/* ── 4 stat tiles ── */}
                    <div className="osc-tiles">
                      <div className="osc-tile tile-purple">
                        <div className="osc-tile-label">Student Shirts</div>
                        <div className="osc-tile-value">{studentCount}</div>
                        <div className="osc-tile-sub">ordered &amp; billed</div>
                      </div>
                      <div className="osc-tile tile-blue">
                        <div className="osc-tile-label">Extra Stock Ordered</div>
                        <div className="osc-tile-value">{extraOrdered}</div>
                        <div className="osc-tile-sub">ordered &amp; billed</div>
                      </div>
                      <div className={`osc-tile ${extraAllocated === extraOrdered && extraOrdered > 0 ? 'tile-green' : 'tile-orange'}`}>
                        <div className="osc-tile-label">Extra Stock Allocated</div>
                        <div className="osc-tile-value">{extraAllocated}</div>
                        <div className="osc-tile-sub">
                          {extraOrdered > 0 ? `${pctAllocated}% distributed` : 'no extra stock'}
                        </div>
                      </div>
                      <div className={`osc-tile ${stillOnHand > 0 ? 'tile-red' : 'tile-green'}`}>
                        <div className="osc-tile-label">Still On Hand</div>
                        <div className="osc-tile-value">{stillOnHand}</div>
                        <div className="osc-tile-sub">
                          {stillOnHand > 0 ? 'in stock — not yet allocated' : 'fully distributed ✓'}
                        </div>
                      </div>
                    </div>

                    {/* ── Progress bar ── */}
                    {extraOrdered > 0 && (
                      <div className="osc-progress-wrap">
                        <div className="osc-progress-label">
                          <span>Extra stock distribution</span>
                          <span>{extraAllocated} of {extraOrdered} allocated</span>
                        </div>
                        <div className="osc-progress-bar">
                          <div
                            className="osc-progress-fill"
                            style={{
                              width: `${pctAllocated}%`,
                              background: pctAllocated === 100 ? '#39B54A' : pctAllocated > 50 ? '#F7941D' : '#ED1C24'
                            }}
                          />
                        </div>
                      </div>
                    )}

                    {/* ── Size breakdown table ── */}
                    {sizeRows.length > 0 && (
                      <div className="osc-size-section">
                        <div className="osc-size-title">📦 Extra Stock Breakdown by Size</div>
                        <div className="table-scroll">
                          <table className="size-breakdown-table">
                            <thead>
                              <tr>
                                <th>Size</th>
                                <th>Ordered</th>
                                <th>Allocated to Students</th>
                                <th>Still In Stock</th>
                                <th>Status</th>
                              </tr>
                            </thead>
                            <tbody>
                              {sizeRows.map(({ size, ordered, allocated, remaining }) => (
                                <tr key={size} className={remaining > 0 ? 'size-row-warning' : 'size-row-done'}>
                                  <td className="size-name-cell">{size}</td>
                                  <td className="size-num-cell">{ordered}</td>
                                  <td className="size-num-cell alloc-cell">{allocated}</td>
                                  <td className={`size-num-cell ${remaining > 0 ? 'remaining-red' : 'remaining-green'}`}>
                                    {remaining}
                                  </td>
                                  <td>
                                    {remaining === 0
                                      ? <span className="status-done">✅ Fully used</span>
                                      : remaining === ordered
                                        ? <span className="status-none">⚠️ None allocated yet</span>
                                        : <span className="status-partial">🔶 Partial — {remaining} remaining</span>
                                    }
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                            <tfoot>
                              <tr className="size-foot">
                                <td><strong>TOTAL</strong></td>
                                <td className="size-num-cell"><strong>{sizeRows.reduce((s, r) => s + r.ordered, 0)}</strong></td>
                                <td className="size-num-cell alloc-cell"><strong>{sizeRows.reduce((s, r) => s + r.allocated, 0)}</strong></td>
                                <td className={`size-num-cell ${stillOnHand > 0 ? 'remaining-red' : 'remaining-green'}`}>
                                  <strong>{stillOnHand}</strong>
                                </td>
                                <td></td>
                              </tr>
                            </tfoot>
                          </table>
                        </div>
                      </div>
                    )}

                    {/* ── Plain-English sentence ── */}
                    <div className="osc-sentence">
                      {studentCount > 0 && (
                        <span><strong>{studentCount}</strong> student shirts were ordered and billed. </span>
                      )}
                      {extraOrdered > 0 && (
                        <span><strong>{extraOrdered}</strong> extra buffer shirts were also ordered and billed. </span>
                      )}
                      {extraOrdered > 0 && extraAllocated > 0 && extraAllocated < extraOrdered && (
                        <span><strong>{extraAllocated}</strong> of these have since been given to students (no extra charge). </span>
                      )}
                      {extraOrdered > 0 && extraAllocated === extraOrdered && (
                        <span>All extra stock has been fully distributed. </span>
                      )}
                      {stillOnHand > 0 && (
                        <span className="sentence-warning">
                          <strong>{stillOnHand} shirts are still sitting in stock</strong> — paid for but not yet allocated to any student.
                        </span>
                      )}
                      {stillOnHand === 0 && extraOrdered > 0 && (
                        <span className="sentence-ok"> Nothing is sitting unused — all stock accounted for. ✅</span>
                      )}
                    </div>

                  </div>
                );
              })
            )}

            {/* ── Grand total ── */}
            {frOrders.length > 0 && (() => {
              const t = frTotals(expandedFranchisee);
              return (
                <div className="fr-grand-total">
                  <span>💳 Total paid by {fr?.name} across all {t.orderCount} order{t.orderCount !== 1 ? 's' : ''}</span>
                  <span className="fr-grand-amt">{fmt(t.totalPaid)}</span>
                </div>
              );
            })()}
          </div>
        );
      })()}

    </div>
  );
}

export default HOTshirtRecon;